import { ref } from 'vue';
import { defineStore } from 'pinia';
import useAxiosInstance from '@/composables/axiosInstance';
import useUISessionStore from './ui-session';
import useDeviceStore from './device';
import AuthenticationSettings from '@/interfaces/AuthenticationSettings';
import AuthenticationQRCode from '@/interfaces/AuthenticationQRCode';
import AuthenticationGracePeriod from '@/interfaces/AuthenticationGracePeriod';
import AuthenticationIsTrustedDevice from '@/interfaces/AuthenticationIsTrustedDevice';

interface State {
  trustedDevice : AuthenticationIsTrustedDevice;
  gracePeriod : AuthenticationGracePeriod;
  settings : AuthenticationSettings;
  QRCode : AuthenticationQRCode;
  QRCodeVerified : boolean;
  secretKey : string | null;
  canTrustThisDevice : boolean;
}

const useTFAuthenticationSettingsStore = defineStore({
  id: 'authentication-settings',
  state: () => ref<State>({
    trustedDevice: {
      isVerifiedEmployee: true,
      isDeviceExpired: false,
    },
    gracePeriod: {
      empCode: '',
      daysToSkip2faSetup: 0,
      canSkip2faSetup: false,
    },
    settings: {
      isEnabledForNewEmployee: false,
      isGracePeriodAllowed: true,
      gracePeriodDays: 0,
      isTrustedDevicesAllowed: false,
      desktopTrustedDays: 0,
      mobileTrustedDays: 0,
      isExpiryDateRecalculated: false,
      instructionsForDesktop: '',
      instructionsForMobile: '',
      informationForAssistance: '',
    } as AuthenticationSettings,
    QRCode: {
      qrCodeSetupImageUrl: '',
      manualEntryKey: '',
    } as AuthenticationQRCode,
    QRCodeVerified: false,
    secretKey: null,
    canTrustThisDevice: false,
  }),
  getters: {
    getIsTrusted(state) : boolean {
      const trusted = !state.trustedDevice.isDeviceExpired && state.trustedDevice.isVerifiedEmployee;
      return trusted;
    },
    getQRCodeImage(state) : string {
      return state.QRCode.qrCodeSetupImageUrl;
    },
    getQRCodeNumber(state) : string {
      return state.QRCode.manualEntryKey;
    },
    getIsTrustedDeviceAllowed(state) : boolean {
      return state.settings.isTrustedDevicesAllowed;
    },
  },
  actions: {
    async initStore() : Promise<State> {
      const uiSessionStore = useUISessionStore();
      if (uiSessionStore.isExpired) {
        await uiSessionStore.forceRefresh();
      }

      await this.fetchVerifyTrustedDevice();
      await this.fetchSettings();
      await this.fetchGracePeriod();
      await this.fetchQRCode();

      return this.$state;
    },
    setTrustedDevice(trustedDevice : AuthenticationIsTrustedDevice) {
      this.trustedDevice = trustedDevice;
    },
    async fetchVerifyTrustedDevice() : Promise<void> {
      const uiSessionStore = useUISessionStore();
      const deviceStore = useDeviceStore();
      const mobileDevice = /Mobi|Android/i.test(navigator.userAgent);
      const { deviceID } = deviceStore;

      if (uiSessionStore.user.empCode) {
        try {
          const { data } = await useAxiosInstance.get<AuthenticationIsTrustedDevice>(`/TwoFactorAuthentication/VerifyTrustedDeviceExpiration?EmpCode=${uiSessionStore.user.empCode}&DeviceUniqueIdentifier=${deviceID}&IsDesktopDevice=${!mobileDevice}`);
          this.trustedDevice = {
            ...data,
          };
        } catch (error : any) {
          console.error(error);
        }
      }
    },
    async fetchGracePeriod() : Promise<void> {
      const uiSessionStore = useUISessionStore();
      try {
        const { data } = await useAxiosInstance.get<AuthenticationGracePeriod>(`/TwoFactorAuthentication/GetEmployeeGracePeriodInfoByEmpCode?EmpCode=${uiSessionStore.user.empCode}`);
        this.gracePeriod = {
          ...data,
        };
      } catch (error : any) {
        console.error(error);
      }
    },
    async fetchSettings() : Promise<void> {
      try {
        const { data } = await useAxiosInstance.get<AuthenticationSettings>('/TwoFactorAuthentication/GetAuthenticationSettings');
        this.settings = {
          ...data,
        };
      } catch (error : any) {
        console.error(error);
      }
    },
    async fetchQRCode() : Promise<void> {
      try {
        const uiSessionStore = useUISessionStore();
        const { data } = await useAxiosInstance.get<AuthenticationQRCode>(`/TwoFactorAuthentication/GetQrCodeAndManualEntryKeyForAuthenticationByEmpCode?EmpCode=${uiSessionStore.user.empCode}`);
        this.QRCode = {
          ...data,
        };
      } catch (error : any) {
        console.error(error);
      }
    },
    updateSecretKey(key : string | null) : void {
      this.secretKey = key;
    },
    updateCanTrustThisDevice(canTrustThisDevice : boolean) {
      this.canTrustThisDevice = canTrustThisDevice;
    },
    async verifyQRCode() : Promise<boolean> {
      const uiSessionStore = useUISessionStore();
      const deviceStore = useDeviceStore();
      const mobileDevice = /Mobi|Android/i.test(navigator.userAgent);

      try {
        const { data } = await useAxiosInstance.post<boolean>('/TwoFactorAuthentication/VerifyAuthenticationKeyByEmpCode', {
          empCode: uiSessionStore.user.empCode,
          secretKey: this.secretKey,
          deviceUniqueIdentifier: deviceStore.deviceID,
          deviceName: deviceStore.deviceOS,
          browserName: deviceStore.deviceBrowser,
          isDesktopDevice: !mobileDevice,
          canTrustThisDevice: this.canTrustThisDevice,
        });

        return data;
      } catch (error : any) {
        console.error(error);
        return false;
      }
    },
  },
});

export default useTFAuthenticationSettingsStore;
